import { useState, useEffect, useRef } from 'react';
import { Building2, ChevronDown, Search, Check, Loader2 } from 'lucide-react';
import { clsx } from 'clsx';
import { useClientStore } from '@/stores/client';

function formatClientName(uid: string): string {
  return uid
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function ClientSelector() {
  const { clients, selectedClientUid, loading, fetchClients, selectClient } = useClientStore();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch once on mount
  useEffect(() => {
    if (clients.length === 0) fetchClients();
  }, []);

  // Close on outside click
  useEffect(() => {
    function onPointerDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  const filtered = clients.filter((c) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    const name = (c.client_name || formatClientName(c.client_uid)).toLowerCase();
    return name.includes(q) || c.client_uid.toLowerCase().includes(q);
  });

  const selectedName = selectedClientUid
    ? (clients.find((c) => c.client_uid === selectedClientUid)?.client_name ||
       formatClientName(selectedClientUid))
    : null;

  function handleToggle() {
    setOpen((o) => !o);
    if (!open) setTimeout(() => inputRef.current?.focus(), 50);
  }

  function handleSelect(uid: string) {
    selectClient(uid);
    setSearch('');
    setOpen(false);
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={handleToggle}
        className={clsx(
          'flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors',
          open
            ? 'border-teal-500 bg-teal-50 text-teal-700 ring-2 ring-teal-500/20'
            : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50',
        )}
      >
        <Building2 size={14} className="text-slate-400 flex-shrink-0" />
        {loading && !selectedName ? (
          <span className="flex items-center gap-1.5 text-slate-400">
            <Loader2 size={12} className="animate-spin" />
            Loading...
          </span>
        ) : (
          <span className={selectedName ? 'text-slate-800' : 'text-slate-400'}>
            {selectedName ?? 'Select client...'}
          </span>
        )}
        <ChevronDown
          size={14}
          className={clsx('text-slate-400 transition-transform', open && 'rotate-180')}
        />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1.5 w-72 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden z-50">
          {/* Search */}
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search clients..."
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-500/30 focus:border-teal-500"
              />
            </div>
          </div>

          {/* List */}
          <div className="max-h-64 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-slate-400">
                <Loader2 size={14} className="animate-spin" />
                Loading clients...
              </div>
            ) : filtered.length === 0 ? (
              <p className="py-6 text-center text-sm text-slate-400">
                {search ? 'No clients match your search' : 'No clients available'}
              </p>
            ) : (
              filtered.map((c) => {
                const name = c.client_name || formatClientName(c.client_uid);
                const isSelected = c.client_uid === selectedClientUid;
                return (
                  <button
                    key={c.client_uid}
                    type="button"
                    onClick={() => handleSelect(c.client_uid)}
                    className={clsx(
                      'w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-left transition-colors',
                      isSelected
                        ? 'bg-teal-50 text-teal-700'
                        : 'text-slate-700 hover:bg-slate-50',
                    )}
                  >
                    <Building2 size={13} className={isSelected ? 'text-teal-500' : 'text-slate-400'} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{name}</div>
                      {name.toLowerCase() !== c.client_uid.toLowerCase() && (
                        <div className="text-[10px] font-mono text-slate-400 truncate">
                          {c.client_uid}
                        </div>
                      )}
                    </div>
                    {isSelected && <Check size={13} className="text-teal-500 flex-shrink-0" />}
                  </button>
                );
              })
            )}
          </div>

          <div className="px-3 py-2 border-t border-slate-100 bg-slate-50">
            <p className="text-[10px] text-slate-400">
              Select a client to scope the workbench
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
